import { AxiosError } from 'axios';
import useForm from 'antd/es/form/hooks/useForm';
import { CommonFormProps, FormModeDependedTexts, InspectionFormFields } from 'types';
import { AppForm, AppFormProps, JournalTeachersPicker } from 'components/organisms';
import {
    DatePickerElement,
    FormNavButtons,
    InspectionIdNumberInputElement,
    InspectionSubjectInputElement,
    SupervisionFormInputElement,
    SupervisorInputElement,
    TeacherPickerVersion,
} from 'components/molecules';
import { commonTexts, journalTexts } from 'consts/text';
import { formFields } from 'consts/form/formFields';
import { useDependentFormFields } from 'hooks/useDependentFormFields/useDependentFormFields';
import { parseDayjsDateIntoString } from 'utils/parsers/dateTime/parseDayjsDateIntoString';

const addModeTexts: FormModeDependedTexts = {
    name: 'inspectionAdd',
    title: journalTexts.journalDetails.common.inspections.addInspection,
    buttonText: commonTexts.actionLabels.add,
};

const editModeTexts: FormModeDependedTexts = {
    name: 'inspectionEdit',
    title: journalTexts.journalDetails.common.inspections.editInspection,
    buttonText: commonTexts.actionLabels.save,
};

type InspectionAddEditFormProps = Omit<
    AppFormProps<InspectionFormFields>,
    'width' | 'title' | 'name' | 'onFinish' | 'error' | 'withGoBack'
> &
    Pick<CommonFormProps, 'mode'> & {
        axiosError?: AxiosError | null;
        isLoading?: boolean;
        onFinish: (inspectionData: InspectionFormFields) => void;
    };
export const InspectionAddEditForm = ({
    mode,
    initialValues,
    axiosError,
    form,
    ...props
}: InspectionAddEditFormProps) => {
    const [formInstance] = useForm<InspectionFormFields>(form);
    const {
        watchedFieldsValues: watchedDateValue,
        handleWatchedFieldsValuesChange: handleWatchedDateValueChange,
    } = useDependentFormFields({
        watchedFields: [formFields.date],
        dependentFields: [formFields.teacherId],
        formInstance,
    });

    const isAddMode = mode === 'add';
    const { name, title, buttonText, buttonIcon } = isAddMode ? addModeTexts : editModeTexts;
    const { date: dateValue } = watchedDateValue;
    const parsedDate = dateValue ? parseDayjsDateIntoString(dateValue, 'YYYY-MM-DD') : undefined;

    const inspectionAddEditError = axiosError ? commonTexts.errorMessages.unknown : '';

    return (
        <AppForm<InspectionFormFields>
            form={formInstance}
            name={name}
            title={title}
            error={inspectionAddEditError}
            withGoBack
            maxWidth={440}
            {...props}
        >
            <InspectionSubjectInputElement />
            <InspectionIdNumberInputElement />
            <DatePickerElement isHalfParentWidth onChange={handleWatchedDateValueChange} />
            <SupervisionFormInputElement />
            <SupervisorInputElement />
            <JournalTeachersPicker
                version={TeacherPickerVersion.INSPECTION}
                date={parsedDate}
                disabled={!parsedDate}
            />
            <FormNavButtons
                formValuesBeforeChanges={initialValues}
                buttonText={buttonText}
                leftIcon={buttonIcon}
                withPlus={isAddMode}
                withDeviceFloppy={!isAddMode}
            />
        </AppForm>
    );
};
